import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import ProtectedRoutes from "./ProtectedRoutes";
import ProtectedLayout from "../layouts/ProtectedLayout";
import UnProtectedLayout from "../layouts/UnProtectedLayout";
import UnProtectedRoutes from "./UnProtectedRoutes";
import { useAuth } from "../contexts/AuthContext";
import { BreadCrumbProvider } from "../contexts/BreadCrumbContext";
import ActivityAwareTokenGuard from "../components/ActivityAwareTokenGuard";
import Loader from "../components/Molecules/Loader/Loader";
import PathTracker from "../components/PathTracker";
import DocumentTitle from "../components/DocumentTitle";

const SiteRoutes: React.FC = () => {
  const { isAuthenticated, isPermissionsLoading } = useAuth();
  return (
    <>
      <PathTracker />
      <DocumentTitle />
      <Routes>
        {/* Root redirect - redirect to login if not authenticated, otherwise to appropriate page based on permissions */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              isPermissionsLoading ? (
                // Show loading while permissions are being fetched
                <Loader isLoading={true} />
              ) : (
                // TODO: Restore permission checking once API issue is resolved
                (() => {
                  // First, check if there's an intended destination (e.g., user accessed protected URL directly)
                  const intendedDestination = sessionStorage.getItem(
                    "intendedDestination",
                  );
                  if (intendedDestination) {
                    // Validate that it's not an unprotected route
                    const isUnprotectedRoute =
                      intendedDestination.includes("/login") ||
                      intendedDestination.includes("/signup") ||
                      intendedDestination.includes("/forgot-password") ||
                      intendedDestination.includes("/forgotpassword") ||
                      intendedDestination.includes("/403") ||
                      intendedDestination === "/";

                    if (!isUnprotectedRoute) {
                      // Do NOT clear intendedDestination here — the root route
                      // may re-render (e.g. when isPermissionsLoading flips) before
                      // the <Navigate> actually completes. Clearing it prematurely
                      // causes the next render to fall through to /domain.
                      // PathTracker will clear it once the user lands on the target route.
                      return <Navigate to={intendedDestination} replace />;
                    }
                    // If it's an unprotected route, clear it and fall through
                    sessionStorage.removeItem("intendedDestination");
                  }

                  // Fall back to last visited path
                  const lastVisitedPath =
                    sessionStorage.getItem("lastVisitedPath");
                  if (
                    lastVisitedPath &&
                    !lastVisitedPath.includes("/login") &&
                    !lastVisitedPath.includes("/403")
                  ) {
                    return <Navigate to={lastVisitedPath} replace />;
                  }
                  return <Navigate to="/domain" replace />;
                })()
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Unprotected routes - only for login, signup, forgot password */}
        <Route
          path="/"
          element={<UnProtectedLayout data={{ name: "VIS", occupation: "" }} />}
        >
          {UnProtectedRoutes()}
        </Route>

        {/* Protected routes - require authentication */}
        <Route
          path="*"
          element={
            <BreadCrumbProvider>
              <ActivityAwareTokenGuard>
                <ProtectedLayout data={{ name: "VIS", occupation: "" }} />
              </ActivityAwareTokenGuard>
            </BreadCrumbProvider>
          }
        >
          {ProtectedRoutes()}
        </Route>
      </Routes>
    </>
  );
};

export default SiteRoutes;
